import { messageOf } from "hooks/useTvApi";
import React, { useEffect, useId, useRef, useState } from "react";
import { Button, Icon, styles as ui } from "stories/Admin";
import styles from "./TvStudioPage.module.scss";

export type Upload = (file: File) => Promise<string>;

/** Velger en bildefil, laster den opp og gir tilbake url-en. */
const usePicker = (upload: Upload, onUploaded: (url: string) => void) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onFile = async (file: File | null | undefined) => {
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Velg en bildefil (JPG eller PNG).");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onUploaded(await upload(file));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const input = (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      className={styles.fileInput}
      tabIndex={-1}
      aria-hidden="true"
      onChange={(e) => onFile(e.currentTarget.files?.[0])}
    />
  );

  return { input, busy, error, open: () => inputRef.current?.click() };
};

interface ImageUploadProps {
  label: string;
  help?: React.ReactNode;
  value: string;
  onChange: (url: string) => void;
  upload: Upload;
}

/** Bildefelt i editoren: miniatyr, «Last opp»/«Bytt» og «Fjern». */
export const ImageUpload: React.FC<ImageUploadProps> = ({
  label,
  help,
  value,
  onChange,
  upload,
}) => {
  const id = useId();
  const picker = usePicker(upload, onChange);
  return (
    <div className={ui.fld}>
      <span className={ui.lbl} id={id}>
        {label}
      </span>
      <div className={styles.upload} aria-labelledby={id} role="group">
        <span className={styles.uploadThumb}>
          {value ? (
            <img src={value} alt="" />
          ) : (
            <Icon name="upload" size={20} strokeWidth={1.8} />
          )}
        </span>
        <span className={styles.uploadActs}>
          <Button
            size="sm"
            variant="secondary"
            loading={picker.busy}
            onClick={picker.open}
          >
            {picker.busy
              ? "Laster opp …"
              : value
                ? "Bytt bilde"
                : "Last opp bilde"}
          </Button>
          {value && !picker.busy && (
            <Button size="sm" variant="ghost" onClick={() => onChange("")}>
              Fjern
            </Button>
          )}
        </span>
        {picker.input}
      </div>
      {picker.error ? (
        <span className={ui.errt} role="alert">
          {picker.error}
        </span>
      ) : (
        help && <span className={ui.help}>{help}</span>
      )}
    </div>
  );
};

export type AttachKind = "qrImageUrl" | "speakerPhotoUrl" | "imageUrl";

export const ATTACH_KINDS: Array<{ kind: AttachKind; label: string }> = [
  { kind: "qrImageUrl", label: "QR-kode" },
  { kind: "speakerPhotoUrl", label: "Bilde av foredragsholder" },
  { kind: "imageUrl", label: "Annet bilde" },
];

export type Attachments = Record<AttachKind, string | null>;

interface AttachMenuProps {
  value: Attachments;
  onChange: (kind: AttachKind, url: string | null) => void;
  upload: Upload;
  disabled?: boolean;
}

/**
 * «Legg ved bilde» til AI-forslag: admin velger type (QR-kode, bilde av
 * foredragsholder eller annet bilde), maks ett av hver.
 */
export const AttachMenu: React.FC<AttachMenuProps> = ({
  value,
  onChange,
  upload,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const kindRef = useRef<AttachKind>("imageUrl");
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const menuId = useId();
  const picker = usePicker(upload, (url) => onChange(kindRef.current, url));
  const free = ATTACH_KINDS.filter((k) => !value[k.kind]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className={styles.attachRow}>
      <span className={styles.attachMenuWrap} ref={wrapRef}>
        <Button
          size="sm"
          variant="secondary"
          loading={picker.busy}
          disabled={disabled || free.length === 0}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          icon={<Icon name="upload" size={14} strokeWidth={2} />}
        >
          {picker.busy ? "Laster opp …" : "Legg ved bilde"}
        </Button>
        {open && (
          <span className={styles.attachMenu} role="menu" id={menuId}>
            {free.map((k) => (
              <button
                key={k.kind}
                type="button"
                role="menuitem"
                onClick={() => {
                  kindRef.current = k.kind;
                  setOpen(false);
                  picker.open();
                }}
              >
                {k.label}
              </button>
            ))}
          </span>
        )}
        {picker.input}
      </span>
      {ATTACH_KINDS.filter((k) => value[k.kind]).map((k) => (
        <span key={k.kind} className={styles.attachChip}>
          <img src={value[k.kind] ?? ""} alt="" />
          <span>{k.label}</span>
          <button
            type="button"
            className={styles.attachRemove}
            aria-label={`Fjern ${k.label.toLowerCase()}`}
            onClick={() => onChange(k.kind, null)}
            disabled={disabled}
          >
            <Icon name="x" size={14} strokeWidth={2.2} />
          </button>
        </span>
      ))}
      {picker.error && (
        <span className={ui.errt} role="alert">
          {picker.error}
        </span>
      )}
    </div>
  );
};
